type AppConfig = {
  apiBaseUrl: string;
  cognitoHostedUiBaseUrl: string;
  cognitoClientId: string;
  redirectUri: string;
  logoutUri: string;
  scope: string;
  storageKey: string;
};

const toCurrencyInput = (amount: number): string => (Number.isFinite(amount) ? amount.toFixed(2) : '0.00');

const toCurrencyLabelFromCents = (amountCents: number): string => `£${(amountCents / 100).toFixed(2)}`;

type SessionRecord = {
  accessToken: string;
  idToken?: string;
  refreshToken: string;
  tokenType: string;
  expiresAt: number;
};

type TokenResponse = {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
};

type ProfileSummary = {
  profile_id: string;
  role: 'owner' | 'editor' | 'viewer';
  linked_at: string;
  description: string;
  icon_name: string;
  theme_color: string;
  setup_completed: boolean;
  reward_balance_cents: number;
  owner_subject: string;
  created_at: string;
  updated_at: string;
};

type ProfilesResponse = {
  items: ProfileSummary[];
  first_login_bootstrap?: boolean;
};

type HabitItem = {
  id: string;
  profile_id: string;
  owner_subject: string;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  sort_order?: number;
  reward_amount: number;
  is_sample?: boolean;
  created_at: string;
  updated_at: string;
};

type HabitsResponse = {
  items: HabitItem[];
};

type HabitEntryItem = {
  id: string;
  profile_id: string;
  habit_id: string;
  scope: 'daily' | 'weekly' | 'monthly';
  bucket: string;
  entry_date: string;
  completed: boolean;
  owner_subject: string;
  created_at: string;
  updated_at: string;
};

type HabitEntriesResponse = {
  items: HabitEntryItem[];
};

type TodoItem = {
  id: string;
  profile_id: string;
  owner_subject: string;
  name: string;
  reward_amount: number;
  sort_order?: number;
  due_date?: string;
  completed: boolean;
  completed_at?: string;
  created_at: string;
  updated_at: string;
};

type TodosResponse = {
  items: TodoItem[];
};

type RewardTransaction = {
  id: string;
  profile_id: string;
  amount_cents: number;
  balance_after_cents: number;
  type: 'habit_completion' | 'habit_reversal' | 'withdrawal' | 'todo_completion' | 'todo_reversal';
  description?: string;
  habit_id?: string;
  habit_entry_id?: string;
  habit_entry_date?: string;
  owner_subject: string;
  created_at: string;
};

type RewardsResponse = {
  balance_cents: number;
  transactions: RewardTransaction[];
};

type UpsertTodosResponse = {
  items: TodoItem[];
  rewards: RewardsResponse;
};

type TrackerDataCache = {
  profileId: string;
  habits: HabitItem[];
  todos: TodoItem[];
  entriesByMonth: Record<string, HabitEntryItem[]>;
};

const cloneEntriesByMonth = (entriesByMonth: Record<string, HabitEntryItem[]>): Record<string, HabitEntryItem[]> =>
  Object.fromEntries(Object.entries(entriesByMonth).map(([monthKey, entries]) => [monthKey, [...entries]]));

let dashboardDayRolloverTimeoutId: number | null = null;
let dashboardDayRolloverFocusListener: (() => void) | null = null;
let dashboardDayRolloverVisibilityListener: (() => void) | null = null;

function clearDashboardDayRolloverWatch(): void {
  if (dashboardDayRolloverTimeoutId !== null) {
    window.clearTimeout(dashboardDayRolloverTimeoutId);
    dashboardDayRolloverTimeoutId = null;
  }
  if (dashboardDayRolloverFocusListener) {
    window.removeEventListener('focus', dashboardDayRolloverFocusListener);
    dashboardDayRolloverFocusListener = null;
  }
  if (dashboardDayRolloverVisibilityListener) {
    document.removeEventListener('visibilitychange', dashboardDayRolloverVisibilityListener);
    dashboardDayRolloverVisibilityListener = null;
  }
}

const PKCE_STORAGE_KEY = 'upperson.auth.pkce';

function getRecentRewardTransactions(transactions: RewardTransaction[]): RewardTransaction[] {
  return [...transactions]
    .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
    .slice(0, 10);
}
const RETURN_TO_STORAGE_KEY = 'upperson.auth.returnTo';
const ACCESS_TOKEN_REFRESH_WINDOW_MS = 30_000;
const DASHBOARD_AUTO_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const app = document.querySelector<HTMLElement>('#app');

if (!app) {
  throw new Error('App container not found');
}

void boot(app).catch((error) => {
  app.innerHTML = `
    <section class="card">
      <p class="eyebrow">Upperson</p>
      <h1>Unexpected error</h1>
      <p class="muted">${escapeHtml(error instanceof Error ? error.message : String(error))}</p>
    </section>
  `;
});

async function boot(root: HTMLElement): Promise<void> {
  renderShell(root, 'Loading', 'Checking authentication state...');

  const config = await loadConfig();
  const url = new URL(window.location.href);

  const oauthError = url.searchParams.get('error');
  if (oauthError) {
    renderFailure(root, 'Login failed', url.searchParams.get('error_description') ?? oauthError, config);
    return;
  }

  const code = url.searchParams.get('code');
  if (code) {
    const state = url.searchParams.get('state');
    await completeLogin(root, config, code, state);
    return;
  }

  const session = loadSession(config.storageKey);
  if (!session) {
    await redirectToLogin(config);
    return;
  }

  const ensuredSession = await ensureValidSession(config, session);
  if (!ensuredSession) {
    clearSession(config.storageKey);
    await redirectToLogin(config);
    return;
  }

  await loadAndRenderProfiles(root, config, ensuredSession);
}

async function loadAndRenderProfiles(root: HTMLElement, config: AppConfig, session: SessionRecord): Promise<void> {
  renderShell(root, 'Loading profile', 'Loading your profiles...');
  const profilesResult = await fetchProfilesWithRetry(config, session);

  if (!profilesResult.session) {
    clearSession(config.storageKey);
    await redirectToLogin(config);
    return;
  }

  saveSession(config.storageKey, profilesResult.session);
  const firstPending = profilesResult.profiles.items.find((item) => !item.setup_completed);
  if (firstPending) {
    await loadAndRenderHabitSetup(
      root,
      config,
      profilesResult.session,
      profilesResult.profiles.items,
      firstPending.profile_id,
      Boolean(profilesResult.profiles.first_login_bootstrap),
    );
    return;
  }

  const preferredActiveProfileId = loadPreferredActiveProfileId(config);
  await loadAndRenderHabitTracker(
    root,
    config,
    profilesResult.session,
    profilesResult.profiles.items,
    preferredActiveProfileId ?? undefined,
  );
}

async function loadAndRenderHabitTracker(
  root: HTMLElement,
  config: AppConfig,
  session: SessionRecord,
  profiles: ProfileSummary[],
  activeProfileId?: string,
  activeWeekStartDateKey?: string,
  activeDateView: TrackerDateView = 'weekly',
  existingCache?: TrackerDataCache,
): Promise<void> {
  const preferredActiveProfileId = activeProfileId ?? loadPreferredActiveProfileId(config) ?? undefined;
  const activeProfile = profiles.find((profile) => profile.profile_id === preferredActiveProfileId) ?? profiles[0];
  if (!activeProfile) {
    throw new Error('No profiles available');
  }
  savePreferredActiveProfileId(config, activeProfile.profile_id);
  const isAutoRefreshStale = hasDashboardDataRefreshExpired(config, activeProfile.profile_id);

  const requestedStart = activeWeekStartDateKey ? parseDateKey(activeWeekStartDateKey) : null;
  const weekStartDateKey =
    activeDateView === 'weekly'
      ? toWeekStartDateKey(requestedStart ?? new Date())
      : toDateKey(requestedStart ?? new Date());
  const weekMeta = resolveWeekMeta(weekStartDateKey);
  const anchorMonthMeta = parseMonthKey(
    requestedStart
      ? `${requestedStart.getUTCFullYear()}-${String(requestedStart.getUTCMonth() + 1).padStart(2, '0')}`
      : weekMeta.days[3]?.monthKey ?? weekMeta.days[0].monthKey,
  ) ?? getCurrentMonthMeta();
  const canReuseCache = existingCache?.profileId === activeProfile.profile_id;
  const shouldUseCache = canReuseCache && !isAutoRefreshStale;
  let currentSession: SessionRecord | null = session;
  let habits: HabitItem[];
  let todos: TodoItem[];
  const entriesByMonth = shouldUseCache ? cloneEntriesByMonth(existingCache.entriesByMonth) : {};
  let fetchedFromServer = false;

  if (shouldUseCache) {
    habits = [...existingCache.habits];
    todos = [...existingCache.todos];
  } else {
    const habitsResult = await fetchHabitsWithRetry(config, session, activeProfile.profile_id);
    if (!habitsResult.session) {
      clearSession(config.storageKey);
      await redirectToLogin(config);
      return;
    }
    habits = habitsResult.habits.items;
    currentSession = habitsResult.session;
    fetchedFromServer = true;

    const todosResult = await fetchTodosWithRetry(config, currentSession, activeProfile.profile_id);
    if (!todosResult.session) {
      clearSession(config.storageKey);
      await redirectToLogin(config);
      return;
    }
    todos = todosResult.todos.items;
    currentSession = todosResult.session;
    fetchedFromServer = true;
  }

  const monthKeys =
    activeDateView === 'weekly' ? Array.from(new Set(weekMeta.days.map((day) => day.monthKey))) : [anchorMonthMeta.key];
  for (const monthKey of monthKeys) {
    if (!entriesByMonth[monthKey]) {
      const entriesResult = await fetchHabitEntriesWithRetry(
        config,
        currentSession,
        activeProfile.profile_id,
        monthKey,
      );
      if (!entriesResult.session) {
        clearSession(config.storageKey);
        await redirectToLogin(config);
        return;
      }
      entriesByMonth[monthKey] = entriesResult.entries.items;
      currentSession = entriesResult.session;
      fetchedFromServer = true;
    }
  }
  if (!currentSession) {
    clearSession(config.storageKey);
    await redirectToLogin(config);
    return;
  }

  const combinedEntries = monthKeys.flatMap((monthKey) => entriesByMonth[monthKey] ?? []);
  const entries = combinedEntries.filter(
    (entry, index, all) => all.findIndex((candidate) => candidate.id === entry.id) === index,
  );
  const nextCache: TrackerDataCache = {
    profileId: activeProfile.profile_id,
    habits: [...habits],
    todos: [...todos],
    entriesByMonth,
  };

  if (fetchedFromServer) {
    saveDashboardDataRefreshedAt(config, activeProfile.profile_id);
  }
  saveSession(config.storageKey, currentSession);
  renderDashboard(
    root,
    config,
    currentSession,
    profiles,
    activeProfile.profile_id,
    habits,
    entries,
    todos,
    anchorMonthMeta,
    weekMeta,
    activeDateView,
    nextCache,
  );
}

async function loadAndRenderRewardLedger(
  root: HTMLElement,
  config: AppConfig,
  session: SessionRecord,
  profiles: ProfileSummary[],
  activeProfileId: string,
  activeWeekStartDateKey: string,
  activeDateView: TrackerDateView,
  existingCache?: TrackerDataCache,
): Promise<void> {
  clearDashboardDayRolloverWatch();
  const activeProfile = profiles.find((profile) => profile.profile_id === activeProfileId) ?? profiles[0];
  if (!activeProfile) {
    throw new Error('No profiles available');
  }

  const rewardsResult = await fetchRewardsWithRetry(config, session, activeProfile.profile_id, 100);
  if (!rewardsResult.session) {
    clearSession(config.storageKey);
    await redirectToLogin(config);
    return;
  }

  saveSession(config.storageKey, rewardsResult.session);
  renderRewardLedgerView(
    root,
    config,
    rewardsResult.session,
    profiles,
    activeProfile.profile_id,
    rewardsResult.rewards,
    activeWeekStartDateKey,
    activeDateView,
    existingCache,
  );
}

async function loadConfig(): Promise<AppConfig> {
  const response = await fetch('./config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load UI config (${response.status})`);
  }

  const data = (await response.json()) as Partial<AppConfig>;
  const required: Array<keyof AppConfig> = [
    'apiBaseUrl',
    'cognitoHostedUiBaseUrl',
    'cognitoClientId',
    'redirectUri',
    'logoutUri',
    'scope',
    'storageKey',
  ];

  for (const key of required) {
    if (typeof data[key] !== 'string' || data[key]?.length === 0) {
      throw new Error(`UI config is missing ${key}`);
    }
  }

  return data as AppConfig;
}

function isDevelopmentEnvironment(config: AppConfig): boolean {
  try {
    const apiHost = new URL(config.apiBaseUrl).hostname.toLowerCase();
    if (apiHost.includes('dev-upperson')) {
      return true;
    }
  } catch {
    // Ignore malformed URL and fall back to current host check.
  }

  return window.location.hostname.toLowerCase().includes('dev-upperson');
}

function getEnvironmentBannerMarkup(config: AppConfig): string {
  if (!isDevelopmentEnvironment(config)) {
    return '';
  }
  return '<div class="environment-banner" role="status" aria-live="polite">Development environment</div>';
}

async function completeLogin(root: HTMLElement, config: AppConfig, code: string, state: string | null): Promise<void> {
  const stored = loadPkceState();
  if (!stored) {
    renderFailure(root, 'Login callback invalid', 'Missing PKCE state. Please sign in again.', config);
    return;
  }

  if (!state || state !== stored.state) {
    renderFailure(root, 'Login callback invalid', 'State mismatch. Please sign in again.', config);
    return;
  }

  const session = await exchangeCodeForTokens(config, code, stored.verifier);
  saveSession(config.storageKey, session);
  clearPkceState();
  replaceUrl(config.redirectUri);
  await loadAndRenderProfiles(root, config, session);
}

function renderShell(root: HTMLElement, title: string, subtitle: string): void {
  clearDashboardDayRolloverWatch();
  root.innerHTML = `
    <section class="card">
      <p class="eyebrow">Upperson</p>
      <h1>${title}</h1>
      <p class="muted">${subtitle}</p>
      <div id="status" class="status">${subtitle}</div>
      <div class="actions">
        <button id="primaryAction" type="button" disabled>Working...</button>
      </div>
      <div class="grid">
        <article class="panel">
          <h2>Session</h2>
          <pre id="sessionView">Loading...</pre>
        </article>
        <article class="panel">
          <h2>Response</h2>
          <pre id="responseView">Waiting...</pre>
        </article>
      </div>
    </section>
  `;
}

function renderFailure(root: HTMLElement, title: string, message: string, config: AppConfig): void {
  clearDashboardDayRolloverWatch();
  const environmentBanner = getEnvironmentBannerMarkup(config);
  root.innerHTML = `
    <section class="card">
      ${environmentBanner}
      <p class="eyebrow">Upperson</p>
      <h1>${title}</h1>
      <p class="muted">${escapeHtml(message)}</p>
      <div id="status" class="status error">${escapeHtml(message)}</div>
      <div class="actions">
        <button id="retryLogin" type="button">Try login again</button>
      </div>
    </section>
  `;

  const button = document.querySelector<HTMLButtonElement>('#retryLogin');
  if (button) {
    button.addEventListener('click', () => {
      void redirectToLogin(config);
    });
  }
}

async function loadAndRenderHabitSetup(
  root: HTMLElement,
  config: AppConfig,
  session: SessionRecord,
  profiles: ProfileSummary[],
  activeProfileId: string,
  firstLoginBootstrap: boolean,
  activeMonthKey?: string,
): Promise<void> {
  clearDashboardDayRolloverWatch();
  const habitsResult = await fetchHabitsWithRetry(config, session, activeProfileId);
  if (!habitsResult.session) {
    clearSession(config.storageKey);
    await redirectToLogin(config);
    return;
  }

  saveSession(config.storageKey, habitsResult.session);
  const activeProfile = profiles.find((profile) => profile.profile_id === activeProfileId) ?? profiles[0];
  if (!activeProfile) {
    throw new Error('No profiles available for setup');
  }

  const rowsByFrequency = toHabitSetupRows(habitsResult.habits.items);
  const environmentBanner = getEnvironmentBannerMarkup(config);

  root.innerHTML = `
    <section class="card">
      ${environmentBanner}
      <p class="eyebrow">Upperson</p>
      <h1>Set up habits</h1>
      <p class="muted">
        ${firstLoginBootstrap ? 'Your starter profile was created. Add daily, weekly, and monthly habits.' : 'Set up habits for your selected profile.'}
      </p>
      <div id="status" class="status">Setup required</div>
      <form id="habitsSetupForm" class="form-grid">
        <label>
          Active profile
          <select id="activeProfileSelect">
            ${profiles
              .map(
                (profile) =>
                  `<option value="${escapeHtml(profile.profile_id)}"${profile.profile_id === activeProfile.profile_id ? ' selected' : ''}>${escapeHtml(profile.description)}</option>`,
              )
              .join('')}
          </select>
        </label>
        <label>
          Profile name
          <input id="habitSetupProfileDescription" type="text" value="${escapeHtml(activeProfile.description)}" placeholder="Home profile" />
        </label>
        <section class="habit-setup-frequency">
          <h2>Daily habits</h2>
          <div id="dailyRows" class="habit-setup-rows"></div>
          <button type="button" class="secondary habit-add-row" data-frequency="daily">+ Add daily habit</button>
        </section>
        <section class="habit-setup-frequency">
          <h2>Weekly habits</h2>
          <div id="weeklyRows" class="habit-setup-rows"></div>
          <button type="button" class="secondary habit-add-row" data-frequency="weekly">+ Add weekly habit</button>
        </section>
        <section class="habit-setup-frequency">
          <h2>Monthly habits</h2>
          <div id="monthlyRows" class="habit-setup-rows"></div>
          <button type="button" class="secondary habit-add-row" data-frequency="monthly">+ Add monthly habit</button>
        </section>
        <div class="actions">
          <button id="saveHabitsSetup" type="submit">Save habits setup</button>
          <button id="backToTrackerFromSetup" type="button" class="secondary">Back to tracker</button>
          <button id="logout" type="button" class="secondary">Logout</button>
        </div>
      </form>
    </section>
  `;

  const form = document.querySelector<HTMLFormElement>('#habitsSetupForm');
  const status = document.querySelector<HTMLElement>('#status');

  const shouldAutoAdvanceCurrentWeek =
    activeDateView === 'weekly' && weekMeta.startDateKey === getCurrentWeekStartDateKey();
  const refreshForDateRollover = (): void => {
    if (!root.querySelector('#prevRangeButton')) {
      clearDashboardDayRolloverWatch();
      return;
    }
    if (toLocalDateKey(new Date()) === todayDateKey) {
      scheduleNextDateRolloverCheck();
      return;
    }
    clearDashboardDayRolloverWatch();
    const nextStartKey = shouldAutoAdvanceCurrentWeek ? getCurrentWeekStartDateKey() : weekMeta.startDateKey;
    void loadAndRenderHabitTracker(
      root,
      config,
      session,
      profiles,
      activeProfile.profile_id,
      nextStartKey,
      activeDateView,
      trackerCache,
    );
  };
  const scheduleNextDateRolloverCheck = (): void => {
    if (dashboardDayRolloverTimeoutId !== null) {
      window.clearTimeout(dashboardDayRolloverTimeoutId);
    }
    dashboardDayRolloverTimeoutId = window.setTimeout(refreshForDateRollover, getMillisecondsUntilNextLocalDay());
  };
  clearDashboardDayRolloverWatch();
  dashboardDayRolloverFocusListener = () => {
    if (!document.hidden) {
      refreshForDateRollover();
    }
  };
  dashboardDayRolloverVisibilityListener = () => {
    if (!document.hidden) {
      refreshForDateRollover();
    }
  };
  window.addEventListener('focus', dashboardDayRolloverFocusListener);
  document.addEventListener('visibilitychange', dashboardDayRolloverVisibilityListener);
  scheduleNextDateRolloverCheck();
  const saveButton = document.querySelector<HTMLButtonElement>('#saveHabitsSetup');
  const logoutButton = document.querySelector<HTMLButtonElement>('#logout');
  const backButton = document.querySelector<HTMLButtonElement>('#backToTrackerFromSetup');
  const profileSelect = document.querySelector<HTMLSelectElement>('#activeProfileSelect');
  const profileDescriptionInput = document.querySelector<HTMLInputElement>('#habitSetupProfileDescription');
  const dailyRowsContainer = document.querySelector<HTMLElement>('#dailyRows');
  const weeklyRowsContainer = document.querySelector<HTMLElement>('#weeklyRows');
  const monthlyRowsContainer = document.querySelector<HTMLElement>('#monthlyRows');
  const addRowButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.habit-add-row'));

  const renderRows = (frequency: HabitSetupFrequency): void => {
    const target =
      frequency === 'daily' ? dailyRowsContainer : frequency === 'weekly' ? weeklyRowsContainer : monthlyRowsContainer;
    if (!target) {
      return;
    }

    target.innerHTML = rowsByFrequency[frequency]
      .map(
        (row, index) => `
        <div class="habit-setup-row" data-frequency="${frequency}" data-index="${index}">
          <input
            type="text"
            value="${escapeHtml(row.name)}"
            placeholder="Habit name"
            data-action="name"
            aria-label="${frequency} habit ${index + 1}"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value="${escapeHtml(toCurrencyInput(row.reward_amount))}"
            placeholder="Reward"
            data-action="reward"
            aria-label="${frequency} reward ${index + 1}"
          />
          <button type="button" class="secondary habit-row-button" data-action="up" ${index === 0 ? 'disabled' : ''}>↑</button>
          <button type="button" class="secondary habit-row-button" data-action="down" ${index === rowsByFrequency[frequency].length - 1 ? 'disabled' : ''}>↓</button>
          <button type="button" class="secondary habit-row-button" data-action="remove">Remove</button>
        </div>`,
      )
      .join('');
  };

  const renderAllRows = (): void => {
    renderRows('daily');
    renderRows('weekly');
    renderRows('monthly');
  };

  if (logoutButton) {
    logoutButton.addEventListener('click', () => {
      void logout(config);
    });
  }

  if (backButton) {
    backButton.addEventListener('click', () => {
      void loadAndRenderHabitTracker(
        root,
        config,
        habitsResult.session!,
        profiles,
        profileSelect?.value ?? activeProfile.profile_id,
        activeMonthKey,
      );
    });
  }

  if (profileSelect) {
    profileSelect.addEventListener('change', () => {
      void loadAndRenderHabitSetup(root, config, habitsResult.session!, profiles, profileSelect.value, false, activeMonthKey);
    });
  }

  for (const button of addRowButtons) {
    button.addEventListener('click', () => {
      const frequency = button.dataset.frequency as HabitSetupFrequency | undefined;
      if (!frequency) {
        return;
      }
      rowsByFrequency[frequency].push({ name: '', frequency, reward_amount: 0 });
      renderRows(frequency);
    });
  }

  const allRowContainers = [dailyRowsContainer, weeklyRowsContainer, monthlyRowsContainer].filter(
    (container): container is HTMLElement => Boolean(container),
  );
  for (const container of allRowContainers) {
    container.addEventListener('input', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.dataset.action !== 'name' && target.dataset.action !== 'reward') {
        return;
      }
      const rowElement = target.closest<HTMLElement>('.habit-setup-row');
      if (!rowElement) {
        return;
      }
      const frequency = rowElement.dataset.frequency as HabitSetupFrequency | undefined;
      const index = Number(rowElement.dataset.index);
      if (!frequency || !Number.isInteger(index) || !rowsByFrequency[frequency][index]) {
        return;
      }
      if (target.dataset.action === 'name') {
        rowsByFrequency[frequency][index].name = target.value;
        return;
      }

      const rewardAmount = Number(target.value);
      rowsByFrequency[frequency][index].reward_amount =
        Number.isFinite(rewardAmount) && rewardAmount >= 0 ? rewardAmount : 0;
    });

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('.habit-row-button');
      if (!button) {
        return;
      }
      const rowElement = button.closest<HTMLElement>('.habit-setup-row');
      if (!rowElement) {
        return;
      }
      const frequency = rowElement.dataset.frequency as HabitSetupFrequency | undefined;
      const index = Number(rowElement.dataset.index);
      const action = button.dataset.action;
      if (!frequency || !Number.isInteger(index)) {
        return;
      }

      const rows = rowsByFrequency[frequency];
      if (action === 'up' && index > 0) {
        [rows[index - 1], rows[index]] = [rows[index], rows[index - 1]];
        renderRows(frequency);
        return;
      }
      if (action === 'down' && index < rows.length - 1) {
        [rows[index + 1], rows[index]] = [rows[index], rows[index + 1]];
        renderRows(frequency);
        return;
      }
      if (action === 'remove') {
        rows.splice(index, 1);
        renderRows(frequency);
      }
    });
  }

  renderAllRows();

  if (!form || !status || !saveButton) {
    return;
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void saveHabitSetup({
      root,
      config,
      session: habitsResult.session!,
      profiles,
      profileId: profileSelect?.value ?? activeProfile.profile_id,
      profileDescription: profileDescriptionInput?.value ?? activeProfile.description,
      rowsByFrequency,
      status,
      saveButton,
    });
  });
}

function renderProfileSetup(
  root: HTMLElement,
  config: AppConfig,
  session: SessionRecord,
  profiles: ProfileSummary[],
  activeProfileId: string,
  activeMonthKey: string,
): void {
  clearDashboardDayRolloverWatch();
  const environmentBanner = getEnvironmentBannerMarkup(config);
  root.innerHTML = `
    <section class="card">
      ${environmentBanner}
      <p class="eyebrow">Upperson</p>
      <h1>Set up profile</h1>
      <p class="muted">Create another profile for habits tracking.</p>
      <div id="status" class="status">Add a profile name, then save.</div>
      <form id="profileSetupForm" class="form-grid">
        <label>
          Profile name
          <input id="newDescription" type="text" placeholder="Home profile" required />
        </label>
        <label>
          Icon name
          <input id="newIconName" type="text" placeholder="account" />
        </label>
        <label>
          Theme color
          <input id="newThemeColor" type="text" placeholder="#1565C0" />
        </label>
        <div class="actions">
          <button id="createProfile" type="submit">Save profile</button>
          <button id="backToTracker" type="button" class="secondary">Back to tracker</button>
          <button id="logout" type="button" class="secondary">Logout</button>
        </div>
      </form>
    </section>
  `;

  const form = document.querySelector<HTMLFormElement>('#profileSetupForm');
  const status = document.querySelector<HTMLElement>('#status');
  const backButton = document.querySelector<HTMLButtonElement>('#backToTracker');
  const logoutButton = document.querySelector<HTMLButtonElement>('#logout');

  if (backButton) {
    backButton.addEventListener('click', () => {
      void loadAndRenderHabitTracker(root, config, session, profiles, activeProfileId, activeMonthKey);
    });
  }

  if (logoutButton) {
    logoutButton.addEventListener('click', () => {
      void logout(config);
    });
  }

  if (!form || !status) {
    return;
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void createProfileAndRefresh(root, config, status, form);
  });
}

type HabitSetupSaveParams = {
  root: HTMLElement;
  config: AppConfig;
  session: SessionRecord;
  profiles: ProfileSummary[];
  profileId: string;
  profileDescription: string;
  rowsByFrequency: HabitSetupRowsByFrequency;
  status: HTMLElement;
  saveButton: HTMLButtonElement;
};

type HabitSetupFrequency = HabitItem['frequency'];
type HabitSetupRow = { id?: string; name: string; frequency: HabitSetupFrequency; reward_amount: number };
type HabitSetupRowsByFrequency = Record<HabitSetupFrequency, HabitSetupRow[]>;

const toHabitSetupRows = (items: HabitItem[]): HabitSetupRowsByFrequency => {
  const grouped: HabitSetupRowsByFrequency = { daily: [], weekly: [], monthly: [] };
  const sorted = [...items].sort((left, right) => {
    const leftOrder = Number.isFinite(left.sort_order) ? Number(left.sort_order) : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right.sort_order) ? Number(right.sort_order) : Number.MAX_SAFE_INTEGER;
    if (left.frequency === right.frequency && leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.updated_at.localeCompare(right.updated_at);
  });

  for (const item of sorted) {
    grouped[item.frequency].push({
      id: item.id,
      name: item.name,
      frequency: item.frequency,
      reward_amount: Number.isFinite(item.reward_amount) ? Number(item.reward_amount) : 0,
    });
  }

  return grouped;
};

async function saveHabitSetup(params: HabitSetupSaveParams): Promise<void> {
  const { root, config, session, profiles, profileId, profileDescription, rowsByFrequency, status, saveButton } = params;
  const selectedProfile = profiles.find((profile) => profile.profile_id === profileId);
  if (!selectedProfile) {
    status.textContent = 'Selected profile not found.';
    return;
  }

  const nextProfileDescription = profileDescription.trim() || selectedProfile.description;

  const toBatchItems = (frequency: HabitSetupFrequency) =>
    rowsByFrequency[frequency]
      .map((row) => ({ ...row, name: row.name.trim() }))
      .filter((row) => row.name.length > 0)
      .map((row, index) => ({
        id: row.id,
        name: row.name,
        frequency,
        sort_order: index + 1,
        reward_amount: row.reward_amount,
      }));

  const items = [
    ...toBatchItems('daily'),
    ...toBatchItems('weekly'),
    ...toBatchItems('monthly'),
  ];

  if (items.length === 0) {
    status.textContent = 'Add at least one habit before saving setup.';
    return;
  }

  saveButton.disabled = true;
  status.textContent = 'Saving habits setup...';

  try {
    const putResponse = await authorizedJsonRequestWithRetry(
      config,
      session,
      `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/habits/batch`,
      {
        method: 'PUT',
        body: JSON.stringify({
          items: items.map((item) => ({
            ...item,
            updated_at: new Date().toISOString(),
          })),
          replace: true,
        }),
      },
    );

    if (!putResponse.session) {
      clearSession(config.storageKey);
      await redirectToLogin(config);
      return;
    }

    const patchResponse = await authorizedJsonRequestWithRetry(
      config,
      putResponse.session,
      `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/profile`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          description: nextProfileDescription,
          setup_completed: true,
        }),
      },
    );

    if (!patchResponse.session) {
      clearSession(config.storageKey);
      await redirectToLogin(config);
      return;
    }

    saveSession(config.storageKey, patchResponse.session);
    status.textContent = 'Habits setup saved. Loading dashboard...';
    await loadAndRenderProfiles(root, config, patchResponse.session);
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : String(error);
  } finally {
    saveButton.disabled = false;
  }
}

type MonthMeta = { key: string; label: string; daysInMonth: number; year: number; monthIndex: number };

type TodayMeta = { dateLabel: string; monthKey: string; dayOfMonth: number; weekOfMonth: number };

type WeekDayMeta = {
  dateKey: string;
  dayOfMonth: number;
  monthKey: string;
  shortLabel: string;
};

type WeekMeta = {
  startDateKey: string;
  endDateKey: string;
  label: string;
  days: WeekDayMeta[];
};

type TrackerDateView = 'weekly' | 'monthly';

function toLocalDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getMillisecondsUntilNextLocalDay(now = new Date()): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1, 0);
  return Math.max(1000, next.getTime() - now.getTime());
}

function toWeekStartDateKey(date: Date): string {
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  utc.setUTCDate(utc.getUTCDate() + mondayOffset);
  return toDateKey(utc);
}

function getCurrentMonthMeta(): MonthMeta {
  const now = new Date();
  const year = now.getFullYear();
  const monthIndex = now.getMonth();
  const month = String(monthIndex + 1).padStart(2, '0');
  return {
    key: `${year}-${month}`,
    label: now.toLocaleString(undefined, { month: 'long', year: 'numeric' }),
    daysInMonth: new Date(year, monthIndex + 1, 0).getDate(),
    year,
    monthIndex,
  };
}

function parseMonthKey(monthKey: string): MonthMeta | null {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
    return null;
  }

  const monthIndex = monthNumber - 1;
  const date = new Date(Date.UTC(year, monthIndex, 1));
  return {
    key: `${year}-${String(monthNumber).padStart(2, '0')}`,
    label: date.toLocaleString(undefined, { month: 'long', year: 'numeric' }),
    daysInMonth: new Date(year, monthNumber, 0).getDate(),
    year,
    monthIndex,
  };
}

function resolveMonthMeta(activeMonthKey?: string): MonthMeta {
  if (!activeMonthKey) {
    return getCurrentMonthMeta();
  }
  return parseMonthKey(activeMonthKey) ?? getCurrentMonthMeta();
}

function buildMonthOptions(selected: MonthMeta, monthsBefore = 12, monthsAfter = 12): MonthMeta[] {
  const options: MonthMeta[] = [];
  for (let offset = -monthsBefore; offset <= monthsAfter; offset += 1) {
    const date = new Date(Date.UTC(selected.year, selected.monthIndex + offset, 1));
    const year = date.getUTCFullYear();
    const monthIndex = date.getUTCMonth();
    const monthNumber = monthIndex + 1;
    options.push({
      key: `${year}-${String(monthNumber).padStart(2, '0')}`,
      label: date.toLocaleString(undefined, { month: 'long', year: 'numeric' }),
      daysInMonth: new Date(year, monthNumber, 0).getDate(),
      year,
      monthIndex,
    });
  }
  return options;
}

function getTodayMeta(): TodayMeta {
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const dayOfMonth = now.getDate();
  return {
    dateLabel: now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    monthKey,
    dayOfMonth,
    weekOfMonth: Math.min(5, Math.floor((dayOfMonth - 1) / 7) + 1),
  };
}

function toMonthIsoDate(monthMeta: MonthMeta, day: number): string {
  return new Date(Date.UTC(monthMeta.year, monthMeta.monthIndex, day)).toISOString();
}

function parseDateKey(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function toDateKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function getCurrentWeekStartDateKey(): string {
  return toWeekStartDateKey(new Date());
}

function resolveWeekMeta(activeWeekStartDateKey?: string): WeekMeta {
  const requestedStart = activeWeekStartDateKey ? parseDateKey(activeWeekStartDateKey) : null;
  const start = requestedStart ?? parseDateKey(getCurrentWeekStartDateKey()) ?? new Date();
  const days: WeekDayMeta[] = [];
  for (let offset = 0; offset < 7; offset += 1) {
    const date = new Date(start.getTime());
    date.setUTCDate(start.getUTCDate() + offset);
    const dateKey = toDateKey(date);
    days.push({
      dateKey,
      dayOfMonth: date.getUTCDate(),
      monthKey: `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`,
      shortLabel: date.toLocaleDateString(undefined, { weekday: 'short' }),
    });
  }
  const end = days[days.length - 1];
  return {
    startDateKey: days[0].dateKey,
    endDateKey: end.dateKey,
    label: `${new Date(`${days[0].dateKey}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} - ${new Date(`${end.dateKey}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`,
    days,
  };
}

function resolveMonthDays(monthMeta: MonthMeta): WeekDayMeta[] {
  const days: WeekDayMeta[] = [];
  for (let day = 1; day <= monthMeta.daysInMonth; day += 1) {
    const date = new Date(Date.UTC(monthMeta.year, monthMeta.monthIndex, day));
    days.push({
      dateKey: toDateKey(date),
      dayOfMonth: day,
      monthKey: monthMeta.key,
      shortLabel: date.toLocaleDateString(undefined, { weekday: 'short' }),
    });
  }
  return days;
}

function toCheckStateFromEntries(entries: HabitEntryItem[]): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const entry of entries) {
    if (!entry.completed) {
      continue;
    }
    const bucket =
      entry.scope === 'daily'
        ? (typeof entry.entry_date === 'string' ? entry.entry_date.slice(0, 10) : entry.bucket)
        : entry.bucket;
    const key = `${entry.scope === 'daily' ? 'd' : entry.scope === 'weekly' ? 'w' : 'm'}:${entry.habit_id}:${bucket}`;
    state[key] = true;
  }
  return state;
}

function toEntryPayloadFromCheckKey(
  profileId: string,
  monthMeta: MonthMeta,
  checkKey: string,
  completed: boolean,
): {
  profileId: string;
  habit_id: string;
  scope: 'daily' | 'weekly' | 'monthly';
  bucket: string;
  entry_date: string;
  completed: boolean;
} | null {
  const [prefix, habitId, bucket] = checkKey.split(':');
  if (!prefix || !habitId || !bucket) {
    return null;
  }

  if (prefix === 'd') {
    const entryDate = parseDateKey(bucket);
    if (!entryDate) {
      return null;
    }
    const day = entryDate.getUTCDate();
    return {
      profileId,
      habit_id: habitId,
      scope: 'daily',
      bucket: String(day),
      entry_date: entryDate.toISOString(),
      completed,
    };
  }

  if (prefix === 'w') {
    const week = Number(bucket);
    if (!Number.isInteger(week) || week < 1 || week > 5) {
      return null;
    }
    const day = Math.min(1 + (week - 1) * 7, monthMeta.daysInMonth);
    return {
      profileId,
      habit_id: habitId,
      scope: 'weekly',
      bucket,
      entry_date: toMonthIsoDate(monthMeta, day),
      completed,
    };
  }

  if (prefix === 'm') {
    return {
      profileId,
      habit_id: habitId,
      scope: 'monthly',
      bucket,
      entry_date: toMonthIsoDate(monthMeta, 1),
      completed,
    };
  }

  return null;
}

function renderRewardLedgerView(
  root: HTMLElement,
  config: AppConfig,
  session: SessionRecord,
  profiles: ProfileSummary[],
  activeProfileId: string,
  rewards: RewardsResponse,
  activeWeekStartDateKey: string,
  activeDateView: TrackerDateView,
  existingCache?: TrackerDataCache,
): void {
  clearDashboardDayRolloverWatch();
  const activeProfile = profiles.find((profile) => profile.profile_id === activeProfileId) ?? profiles[0];
  if (!activeProfile) {
    return;
  }

  const transactions = [...rewards.transactions].sort(
    (left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
  );
  const environmentBanner = getEnvironmentBannerMarkup(config);

  root.innerHTML = `
    <section class="card">
      ${environmentBanner}
      <p class="eyebrow">Upperson</p>
      <div class="actions">
        <button id="backToDashboard" type="button">← Back to tracker</button>
      </div>
      <div class="ledger-view-panel">
        <div class="ledger-view-header">
          <h1>Reward ledger</h1>
          <p class="muted">Showing ${transactions.length} transaction${transactions.length === 1 ? '' : 's'} for ${escapeHtml(activeProfile.description)}.</p>
        </div>
        <div class="ledger-scroll-window">
          <table class="tracker-table tracker-table--rewards-ledger">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              ${
                transactions.length === 0
                  ? '<tr><td colspan="5">No reward transactions yet.</td></tr>'
                  : transactions
                      .map((transaction) => {
                        const amountSign = transaction.amount_cents >= 0 ? '+' : '-';
                        const amountLabel = `${amountSign}${toCurrencyLabelFromCents(Math.abs(transaction.amount_cents))}`;
                        const createdLabel = new Date(transaction.created_at).toLocaleString();
                        return `<tr>
                          <td>${escapeHtml(createdLabel)}</td>
                          <td>${escapeHtml(transaction.type)}</td>
                          <td>${escapeHtml(transaction.description ?? '-')}</td>
                          <td>${escapeHtml(amountLabel)}</td>
                          <td>${escapeHtml(toCurrencyLabelFromCents(transaction.balance_after_cents))}</td>
                        </tr>`;
                      })
                      .join('')
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
  `;

  const backButton = document.querySelector<HTMLButtonElement>('#backToDashboard');
  if (backButton) {
    backButton.addEventListener('click', () => {
      void loadAndRenderHabitTracker(
        root,
        config,
        session,
        profiles,
        activeProfileId,
        activeWeekStartDateKey,
        activeDateView,
        existingCache,
      );
    });
  }
}

function renderDashboard(
  root: HTMLElement,
  config: AppConfig,
  session: SessionRecord,
  profiles: ProfileSummary[],
  activeProfileId: string,
  habits: HabitItem[],
  entries: HabitEntryItem[],
  todos: TodoItem[],
  monthMeta: MonthMeta,
  weekMeta: WeekMeta,
  activeDateView: TrackerDateView,
  existingCache: TrackerDataCache,
): void {
  const activeProfile = profiles.find((profile) => profile.profile_id === activeProfileId) ?? profiles[0];
  if (!activeProfile) {
    return;
  }

  const sortByDisplayOrder = (left: HabitItem, right: HabitItem): number => {
    const leftOrder = Number.isFinite(left.sort_order) ? Number(left.sort_order) : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right.sort_order) ? Number(right.sort_order) : Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.updated_at.localeCompare(right.updated_at);
  };

  const checkState = toCheckStateFromEntries(entries);
  const dailyHabits = habits.filter((habit) => habit.frequency === 'daily').sort(sortByDisplayOrder);
  const weeklyHabits = habits.filter((habit) => habit.frequency === 'weekly').sort(sortByDisplayOrder);
  const monthlyHabits = habits.filter((habit) => habit.frequency === 'monthly').sort(sortByDisplayOrder);
  const today = getTodayMeta();
  const todayDateKey = toLocalDateKey(new Date());
  const isCurrentMonth = monthMeta.key === today.monthKey;
  const highlightedWeek = isCurrentMonth ? today.weekOfMonth : null;
  const monthDays = resolveMonthDays(monthMeta);
  const displayedDailyDays = activeDateView === 'weekly' ? weekMeta.days : monthDays;
  const dateRangeLabel = activeDateView === 'weekly' ? weekMeta.label : monthMeta.label;
  const rewardBalanceCents = Number.isFinite(activeProfile.reward_balance_cents)
    ? Math.round(activeProfile.reward_balance_cents)
    : 0;
  const allTodos = [...todos].sort((left, right) => {
    const leftOrder = Number.isFinite(left.sort_order) ? Number(left.sort_order) : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right.sort_order) ? Number(right.sort_order) : Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.updated_at.localeCompare(right.updated_at);
  });
  const trackerCache: TrackerDataCache = {
    profileId: activeProfile.profile_id,
    habits: [...habits],
    todos: [...allTodos],
    entriesByMonth: cloneEntriesByMonth(existingCache.entriesByMonth),
  };
  const visibleTodos = allTodos.filter((todo) => {
    if (!todo.completed) {
      return true;
    }
    if (!todo.completed_at) {
      return false;
    }
    const completedAt = Date.parse(todo.completed_at);
    if (Number.isNaN(completedAt)) {
      return false;
    }
    return Date.now() - completedAt < 24 * 60 * 60 * 1000;
  });
  const environmentBanner = getEnvironmentBannerMarkup(config);

  root.innerHTML = `
    <section class="card">
      ${environmentBanner}
      <div class="dashboard-topbar">
        <div class="dashboard-topbar__start">
          <button id="mobileNavToggle" type="button" class="secondary menu-toggle" aria-expanded="false" aria-controls="dashboardMenuPanel">☰</button>
          <div class="dashboard-brand">
            <p class="eyebrow">Upperson</p>
            <p class="today-date">Today: ${escapeHtml(today.dateLabel)}</p>
          </div>
        </div>
        <div class="dashboard-topbar__actions">
          <div class="actions toolbar-actions">
            <button id="openWithdrawRewards" type="button" class="icon-button" aria-label="Withdraw rewards" title="Withdraw rewards">💸</button>
            <button id="printTracker" type="button" class="icon-button" aria-label="Print tracker" title="Print tracker">🖨️</button>
            <button id="refreshProfiles" type="button" class="icon-button" aria-label="Refresh data" title="Refresh data">↻</button>
          </div>
        </div>
      </div>
      <div id="dashboardMenuPanel" class="menu-panel" hidden>
        <button id="openProfileSetup" type="button">Setup profile</button>
        <button id="openHabitSetup" type="button">Setup habits</button>
        <button id="openRewardLedger" type="button">Reward ledger</button>
        <button id="logout" type="button" class="secondary">Logout</button>
      </div>
      <div id="status" class="visually-hidden" aria-live="polite"></div>
      <div class="tracker-controls">
        <div class="tracker-control-with-action">
          <label class="tracker-control">
            Active profile
            <select id="dashboardActiveProfile">
              ${profiles
                .map(
                  (profile) =>
                    `<option value="${escapeHtml(profile.profile_id)}"${profile.profile_id === activeProfile.profile_id ? ' selected' : ''}>${escapeHtml(profile.description)}</option>`,
                )
                .join('')}
            </select>
          </label>
          <button id="openAddTodoDialog" type="button" class="secondary tracker-add-todo">+ Todo</button>
        </div>
        <div class="tracker-summary-card">
          <div class="rewards-summary">
            <p>${escapeHtml(toCurrencyLabelFromCents(rewardBalanceCents))}</p>
          </div>
        </div>
      </div>
      <div class="tracker-section todo-section">
        <div class="table-wrap">
          <table class="tracker-table tracker-table--todos">
            <thead>
              <tr>
                <th>Done</th>
                <th>Task</th>
                <th>Due</th>
                <th>Reward</th>
                <th>Priority</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              ${
                visibleTodos.length === 0
                  ? '<tr><td colspan="6">No todos to show.</td></tr>'
                  : visibleTodos
                      .map((todo, index) => {
                        const isOverdue = Boolean(todo.due_date) && !todo.completed && todo.due_date < todayDateKey;
                        const rowClass = `${todo.completed ? 'todo-row--completed' : ''} ${isOverdue ? 'todo-row--overdue' : ''}`.trim();
                        const dueLabel = todo.due_date ? new Date(`${todo.due_date}T00:00:00`).toLocaleDateString() : '-';
                        return `<tr class="${rowClass}" data-todo-id="${escapeHtml(todo.id)}">
                          <td><input type="checkbox" class="todo-checkbox" data-todo-id="${escapeHtml(todo.id)}"${todo.completed ? ' checked' : ''} /></td>
                          <td>${escapeHtml(todo.name)}</td>
                          <td>${escapeHtml(dueLabel)}</td>
                          <td>${escapeHtml(toCurrencyLabelFromCents(Math.round(todo.reward_amount * 100)))}</td>
                          <td>
                            <button type="button" class="secondary todo-priority-button" data-todo-action="up" data-todo-id="${escapeHtml(todo.id)}" ${index === 0 ? 'disabled' : ''}>↑</button>
                            <button type="button" class="secondary todo-priority-button" data-todo-action="down" data-todo-id="${escapeHtml(todo.id)}" ${index === visibleTodos.length - 1 ? 'disabled' : ''}>↓</button>
                          </td>
                          <td>
                            <button type="button" class="secondary todo-delete-button" data-todo-id="${escapeHtml(todo.id)}">✕</button>
                          </td>
                        </tr>`;
                      })
                      .join('')
              }
            </tbody>
          </table>
        </div>
      </div>
      <div class="tracker-section">
        <div class="week-nav">
          <h2>Daily habits (${escapeHtml(dateRangeLabel)})</h2>
          <div class="week-nav-actions">
            <button id="toggleDateRangeButton" type="button" class="secondary">${activeDateView === 'weekly' ? 'Switch to monthly view' : 'Switch to weekly view'}</button>
            <button id="prevRangeButton" type="button" class="secondary">← Previous ${activeDateView === 'weekly' ? 'week' : 'month'}</button>
            <button id="nextRangeButton" type="button" class="secondary">Next ${activeDateView === 'weekly' ? 'week' : 'month'} →</button>
          </div>
        </div>
        <div class="table-wrap ${activeDateView === 'monthly' ? 'table-wrap--wide' : ''}">
          <table class="tracker-table tracker-table--daily ${activeDateView === 'monthly' ? 'tracker-table--daily-month' : ''}">
            <thead>
              <tr>
                <th>Daily habits</th>
                ${displayedDailyDays
                  .map((day) => {
                    const highlightClass = day.dateKey === todayDateKey ? 'tracker-highlight-today' : '';
                    return `<th class="${highlightClass}">${escapeHtml(day.shortLabel)} ${day.dayOfMonth}</th>`;
                  })
                  .join('')}
              </tr>
            </thead>
            <tbody>
              ${dailyHabits
                .map(
                  (habit) => `
                <tr>
                  <td>${escapeHtml(habit.name)}</td>
                  ${displayedDailyDays
                    .map((day) => {
                      const key = `d:${habit.id}:${day.dateKey}`;
                      const checked = checkState[key] ? ' checked' : '';
                      const highlightClass = day.dateKey === todayDateKey ? 'tracker-highlight-today' : '';
                      return `<td class="${highlightClass}"><input type="checkbox" class="track-checkbox" data-check-key="${key}"${checked} /></td>`;
                    })
                    .join('')}
                </tr>`,
                )
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
      <div class="tracker-section">
        <h2>Weekly habits</h2>
        <div class="table-wrap">
          <table class="tracker-table tracker-table--weekly">
            <thead>
              <tr>
                <th>Weekly habits</th>
                ${Array.from({ length: 5 }, (_, index) => {
                  const week = index + 1;
                  const highlightClass = highlightedWeek === week ? 'tracker-highlight-week' : '';
                  return `<th class="${highlightClass}">Week ${week}</th>`;
                }).join('')}
              </tr>
            </thead>
            <tbody>
              ${
              weeklyHabits.length === 0
                ? '<tr><td colspan="6">No weekly habits yet.</td></tr>'
                : weeklyHabits
                    .map((habit) => {
                      const cells = Array.from({ length: 5 }, (_, index) => {
                        const week = index + 1;
                        const key = `w:${habit.id}:${week}`;
                        const checked = checkState[key] ? ' checked' : '';
                        const highlightClass = highlightedWeek === week ? 'tracker-highlight-week' : '';
                        return `<td class="${highlightClass}"><input type="checkbox" class="track-checkbox" data-check-key="${key}"${checked} /></td>`;
                      }).join('');
                      return `<tr><td>${escapeHtml(habit.name)}</td>${cells}</tr>`;
                    })
                    .join('')
              }
            </tbody>
          </table>
        </div>
      </div>
      <div class="tracker-section">
        <h2>Monthly habits</h2>
        <div class="table-wrap">
          <table class="tracker-table tracker-table--monthly">
            <thead><tr><th>Monthly habits</th><th>Done</th></tr></thead>
            <tbody>
              ${
              monthlyHabits.length === 0
                ? '<tr><td colspan="2">No monthly habits yet.</td></tr>'
                : monthlyHabits
                    .map((habit) => {
                      const key = `m:${habit.id}:1`;
                      const checked = checkState[key] ? ' checked' : '';
                      return `<tr><td>${escapeHtml(habit.name)}</td><td><input type="checkbox" class="track-checkbox" data-check-key="${key}"${checked} /></td></tr>`;
                    })
                    .join('')
              }
            </tbody>
          </table>
        </div>
      </div>
      <article class="panel notes-panel">
        <h2>Notes</h2>
        <p class="notes-copy">"Success is the product of daily habits - not once-in-a-lifetime transformations."</p>
      </article>
      <dialog id="todoCreateDialog" class="todo-dialog">
        <form id="todoCreateForm" method="dialog" class="form-grid">
          <h2>Add todo</h2>
          <label>
            Task
            <input id="todoNameInput" type="text" maxlength="140" required />
          </label>
          <label>
            Reward (£)
            <input id="todoRewardInput" type="number" min="0" step="0.01" value="0.00" />
          </label>
          <label>
            Due date (optional)
            <input id="todoDueDateInput" type="date" />
          </label>
          <div class="actions">
            <button id="saveTodoButton" type="submit">Save todo</button>
            <button id="cancelTodoCreateButton" type="button" class="secondary">Cancel</button>
          </div>
        </form>
      </dialog>
      <dialog id="todoDeleteDialog" class="todo-dialog">
        <form id="todoDeleteForm" method="dialog" class="form-grid">
          <h2>Delete todo</h2>
          <p id="todoDeleteMessage" class="muted">Are you sure you want to delete this todo?</p>
          <div class="actions">
            <button id="confirmTodoDeleteButton" type="submit">Delete</button>
            <button id="cancelTodoDeleteButton" type="button" class="secondary">Cancel</button>
          </div>
        </form>
      </dialog>
      <dialog id="withdrawDialog" class="withdraw-dialog">
        <form id="withdrawRewardsForm" method="dialog" class="form-grid">
          <h2>Withdraw rewards</h2>
          <div class="withdraw-summary">
            <p>Current total: <strong id="withdrawCurrentTotal">${escapeHtml(toCurrencyLabelFromCents(rewardBalanceCents))}</strong></p>
            <p>Remaining after withdraw: <strong id="withdrawRemainingTotal">${escapeHtml(toCurrencyLabelFromCents(rewardBalanceCents))}</strong></p>
          </div>
          <label>
            Amount (£)
            <input id="withdrawAmount" type="number" min="0.01" step="0.01" required />
          </label>
          <label>
            Description (optional)
            <input id="withdrawDescription" type="text" maxlength="120" placeholder="Pocket money payout" />
          </label>
          <div class="actions">
            <button id="confirmWithdrawRewards" type="submit">Withdraw</button>
            <button id="cancelWithdrawRewards" type="button" class="secondary">Cancel</button>
          </div>
        </form>
      </dialog>
    </section>
  `;

  const refreshProfilesButton = document.querySelector<HTMLButtonElement>('#refreshProfiles');
  const mobileNavToggle = document.querySelector<HTMLButtonElement>('#mobileNavToggle');
  const dashboardMenuPanel = document.querySelector<HTMLElement>('#dashboardMenuPanel');
  const openProfileSetupButton = document.querySelector<HTMLButtonElement>('#openProfileSetup');
  const openHabitSetupButton = document.querySelector<HTMLButtonElement>('#openHabitSetup');
  const openRewardLedgerButton = document.querySelector<HTMLButtonElement>('#openRewardLedger');
  const openWithdrawRewardsButton = document.querySelector<HTMLButtonElement>('#openWithdrawRewards');
  const openAddTodoDialogButton = document.querySelector<HTMLButtonElement>('#openAddTodoDialog');
  const prevRangeButton = document.querySelector<HTMLButtonElement>('#prevRangeButton');
  const nextRangeButton = document.querySelector<HTMLButtonElement>('#nextRangeButton');
  const toggleDateRangeButton = document.querySelector<HTMLButtonElement>('#toggleDateRangeButton');
  const printTrackerButton = document.querySelector<HTMLButtonElement>('#printTracker');
  const todoCheckboxes = Array.from(document.querySelectorAll<HTMLInputElement>('.todo-checkbox'));
  const todoPriorityButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.todo-priority-button'));
  const todoDeleteButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.todo-delete-button'));
  const todoCreateDialog = document.querySelector<HTMLDialogElement>('#todoCreateDialog');
  const todoCreateForm = document.querySelector<HTMLFormElement>('#todoCreateForm');
  const todoNameInput = document.querySelector<HTMLInputElement>('#todoNameInput');
  const todoRewardInput = document.querySelector<HTMLInputElement>('#todoRewardInput');
  const todoDueDateInput = document.querySelector<HTMLInputElement>('#todoDueDateInput');
  const cancelTodoCreateButton = document.querySelector<HTMLButtonElement>('#cancelTodoCreateButton');
  const todoDeleteDialog = document.querySelector<HTMLDialogElement>('#todoDeleteDialog');
  const todoDeleteForm = document.querySelector<HTMLFormElement>('#todoDeleteForm');
  const todoDeleteMessage = document.querySelector<HTMLElement>('#todoDeleteMessage');
  const cancelTodoDeleteButton = document.querySelector<HTMLButtonElement>('#cancelTodoDeleteButton');
  const withdrawDialog = document.querySelector<HTMLDialogElement>('#withdrawDialog');
  const withdrawForm = document.querySelector<HTMLFormElement>('#withdrawRewardsForm');
  const withdrawAmount = document.querySelector<HTMLInputElement>('#withdrawAmount');
  const withdrawDescription = document.querySelector<HTMLInputElement>('#withdrawDescription');
  const withdrawCurrentTotal = document.querySelector<HTMLElement>('#withdrawCurrentTotal');
  const withdrawRemainingTotal = document.querySelector<HTMLElement>('#withdrawRemainingTotal');
  const cancelWithdrawButton = document.querySelector<HTMLButtonElement>('#cancelWithdrawRewards');
  const logoutButton = document.querySelector<HTMLButtonElement>('#logout');
  const activeProfileSelect = document.querySelector<HTMLSelectElement>('#dashboardActiveProfile');
  const checkboxes = Array.from(document.querySelectorAll<HTMLInputElement>('.track-checkbox'));
  const status = document.querySelector<HTMLElement>('#status');

  const closeDashboardMenu = (): void => {
    if (dashboardMenuPanel) {
      dashboardMenuPanel.hidden = true;
    }
    if (mobileNavToggle) {
      mobileNavToggle.setAttribute('aria-expanded', 'false');
    }
  };

  if (mobileNavToggle && dashboardMenuPanel) {
    mobileNavToggle.addEventListener('click', () => {
      const isOpen = !dashboardMenuPanel.hidden;
      dashboardMenuPanel.hidden = isOpen;
      mobileNavToggle.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  if (refreshProfilesButton) {
    refreshProfilesButton.addEventListener('click', () => {
      closeDashboardMenu();
      void loadAndRenderProfiles(root, config, session);
    });
  }

  if (openProfileSetupButton) {
    openProfileSetupButton.addEventListener('click', () => {
      closeDashboardMenu();
      renderProfileSetup(root, config, session, profiles, activeProfile.profile_id, weekMeta.startDateKey);
    });
  }

  if (openHabitSetupButton) {
    openHabitSetupButton.addEventListener('click', () => {
      closeDashboardMenu();
      void loadAndRenderHabitSetup(root, config, session, profiles, activeProfile.profile_id, false, weekMeta.startDateKey);
    });
  }

  if (openRewardLedgerButton) {
    openRewardLedgerButton.addEventListener('click', () => {
      closeDashboardMenu();
      void loadAndRenderRewardLedger(
        root,
        config,
        session,
        profiles,
        activeProfile.profile_id,
        weekMeta.startDateKey,
        activeDateView,
        trackerCache,
      );
    });
  }

  if (toggleDateRangeButton) {
    toggleDateRangeButton.addEventListener('click', () => {
      const nextView: TrackerDateView = activeDateView === 'weekly' ? 'monthly' : 'weekly';
      const currentMonth = getCurrentMonthMeta();
      const isCurrentMonth = monthMeta.key === currentMonth.key;
      const nextDateKey =
        nextView === 'monthly'
          ? weekMeta.startDateKey
          : isCurrentMonth
            ? getCurrentWeekStartDateKey()
            : toWeekStartDateKey(new Date(Date.UTC(monthMeta.year, monthMeta.monthIndex, 1)));
      void loadAndRenderHabitTracker(
        root,
        config,
        session,
        profiles,
        activeProfile.profile_id,
        nextDateKey,
        nextView,
        trackerCache,
      );
    });
  }

  if (prevRangeButton) {
    prevRangeButton.addEventListener('click', () => {
      let nextStartKey = weekMeta.startDateKey;
      if (activeDateView === 'weekly') {
        const currentStart = parseDateKey(weekMeta.startDateKey);
        if (!currentStart) {
          return;
        }
        currentStart.setUTCDate(currentStart.getUTCDate() - 7);
        nextStartKey = toDateKey(currentStart);
      } else {
        const monthDate = new Date(Date.UTC(monthMeta.year, monthMeta.monthIndex - 1, 1));
        nextStartKey = toDateKey(monthDate);
      }
      void loadAndRenderHabitTracker(
        root,
        config,
        session,
        profiles,
        activeProfile.profile_id,
        nextStartKey,
        activeDateView,
        trackerCache,
      );
    });
  }

  if (nextRangeButton) {
    nextRangeButton.addEventListener('click', () => {
      let nextStartKey = weekMeta.startDateKey;
      if (activeDateView === 'weekly') {
        const currentStart = parseDateKey(weekMeta.startDateKey);
        if (!currentStart) {
          return;
        }
        currentStart.setUTCDate(currentStart.getUTCDate() + 7);
        nextStartKey = toDateKey(currentStart);
      } else {
        const monthDate = new Date(Date.UTC(monthMeta.year, monthMeta.monthIndex + 1, 1));
        nextStartKey = toDateKey(monthDate);
      }
      void loadAndRenderHabitTracker(
        root,
        config,
        session,
        profiles,
        activeProfile.profile_id,
        nextStartKey,
        activeDateView,
        trackerCache,
      );
    });
  }

  const isVisibleTodo = (todo: TodoItem): boolean => {
    if (!todo.completed) {
      return true;
    }
    if (!todo.completed_at) {
      return false;
    }
    const completedAt = Date.parse(todo.completed_at);
    if (Number.isNaN(completedAt)) {
      return false;
    }
    return Date.now() - completedAt < 24 * 60 * 60 * 1000;
  };

  const buildTodoBatchItems = (source: TodoItem[]) =>
    source.map((todo, index) => ({
      id: todo.id,
      name: todo.name,
      reward_amount: todo.reward_amount,
      due_date: todo.due_date,
      completed: todo.completed,
      sort_order: index + 1,
    }));

  const sortTodosByDisplayOrder = (source: TodoItem[]): TodoItem[] =>
    [...source].sort((left, right) => {
      const leftOrder = Number.isFinite(left.sort_order) ? Number(left.sort_order) : Number.MAX_SAFE_INTEGER;
      const rightOrder = Number.isFinite(right.sort_order) ? Number(right.sort_order) : Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.updated_at.localeCompare(right.updated_at);
    });

  const mergeTodosById = (base: TodoItem[], updates: TodoItem[]): TodoItem[] => {
    const merged = new Map(base.map((todo) => [todo.id, todo] as const));
    for (const todo of updates) {
      merged.set(todo.id, todo);
    }
    return sortTodosByDisplayOrder(Array.from(merged.values()));
  };

  const buildTrackerCache = (nextTodos: TodoItem[], nextEntriesByMonth?: Record<string, HabitEntryItem[]>): TrackerDataCache => ({
    profileId: activeProfile.profile_id,
    habits: [...habits],
    todos: sortTodosByDisplayOrder(nextTodos),
    entriesByMonth: cloneEntriesByMonth(nextEntriesByMonth ?? trackerCache.entriesByMonth),
  });

  const updateProfileBalanceFromRewards = (currentProfiles: ProfileSummary[], rewards: RewardsResponse): ProfileSummary[] =>
    currentProfiles.map((profile) =>
      profile.profile_id === activeProfile.profile_id ? { ...profile, reward_balance_cents: rewards.balance_cents } : profile,
    );

  if (openAddTodoDialogButton && todoCreateDialog && todoNameInput && todoRewardInput) {
    openAddTodoDialogButton.addEventListener('click', () => {
      if (todoDueDateInput) {
        todoDueDateInput.value = '';
      }
      todoNameInput.value = '';
      todoRewardInput.value = '0.00';
      if (typeof todoCreateDialog.showModal === 'function') {
        todoCreateDialog.showModal();
      } else {
        todoCreateDialog.setAttribute('open', 'true');
      }
    });
  }

  if (cancelTodoCreateButton && todoCreateDialog) {
    cancelTodoCreateButton.addEventListener('click', () => {
      todoCreateDialog.close();
    });
  }

  if (todoCreateForm && todoCreateDialog && todoNameInput && todoRewardInput) {
    todoCreateForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const name = todoNameInput.value.trim();
      const rewardRaw = Number(todoRewardInput.value);
      const rewardAmount = Number.isFinite(rewardRaw) && rewardRaw >= 0 ? rewardRaw : 0;
      if (!name) {
        return;
      }
      const dueDate = todoDueDateInput?.value?.trim();
      void (async () => {
        try {
          const response = await upsertTodosWithRetry(config, session, activeProfile.profile_id, {
            items: [
              {
                name,
                reward_amount: rewardAmount,
                due_date: dueDate ? dueDate : undefined,
                completed: false,
                sort_order: allTodos.length + 1,
              },
            ],
            replace: false,
          });
          if (!response.session) {
            clearSession(config.storageKey);
            await redirectToLogin(config);
            return;
          }
          saveSession(config.storageKey, response.session);
          todoCreateDialog.close();
          const updatedProfiles = updateProfileBalanceFromRewards(profiles, response.todos.rewards);
          const nextTodos = mergeTodosById(allTodos, response.todos.items);
          await loadAndRenderHabitTracker(
            root,
            config,
            response.session,
            updatedProfiles,
            activeProfile.profile_id,
            weekMeta.startDateKey,
            activeDateView,
            buildTrackerCache(nextTodos),
          );
        } catch (error) {
          if (status) {
            status.textContent = error instanceof Error ? error.message : String(error);
            status.classList.add('error');
          }
        }
      })();
    });
  }

  let pendingDeleteTodoId: string | null = null;
  let isApplyingTodoChange = false;
  let isApplyingHabitChange = false;

  if (cancelTodoDeleteButton && todoDeleteDialog) {
    cancelTodoDeleteButton.addEventListener('click', () => {
      pendingDeleteTodoId = null;
      todoDeleteDialog.close();
    });
  }

  for (const deleteButton of todoDeleteButtons) {
    deleteButton.addEventListener('click', () => {
      const todoId = deleteButton.dataset.todoId;
      if (!todoId || !todoDeleteDialog) {
        return;
      }
      pendingDeleteTodoId = todoId;
      const todo = allTodos.find((item) => item.id === todoId);
      if (todoDeleteMessage) {
        todoDeleteMessage.textContent = `Are you sure you want to delete "${todo?.name ?? 'this todo'}"?`;
      }
      if (typeof todoDeleteDialog.showModal === 'function') {
        todoDeleteDialog.showModal();
      } else {
        todoDeleteDialog.setAttribute('open', 'true');
      }
    });
  }

  if (todoDeleteForm && todoDeleteDialog) {
    todoDeleteForm.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!pendingDeleteTodoId) {
        return;
      }
      const deletingTodoId = pendingDeleteTodoId;
      pendingDeleteTodoId = null;
      void (async () => {
        try {
          const response = await deleteTodoWithRetry(config, session, activeProfile.profile_id, deletingTodoId);
          if (!response.session) {
            clearSession(config.storageKey);
            await redirectToLogin(config);
            return;
          }
          saveSession(config.storageKey, response.session);
          todoDeleteDialog.close();
          const nextTodos = allTodos.filter((todo) => todo.id !== deletingTodoId);
          await loadAndRenderHabitTracker(
            root,
            config,
            response.session,
            profiles,
            activeProfile.profile_id,
            weekMeta.startDateKey,
            activeDateView,
            buildTrackerCache(nextTodos),
          );
        } catch (error) {
          if (status) {
            status.textContent = error instanceof Error ? error.message : String(error);
            status.classList.add('error');
          }
        }
      })();
    });
  }

  for (const todoCheckbox of todoCheckboxes) {
    todoCheckbox.addEventListener('change', () => {
      const todoId = todoCheckbox.dataset.todoId;
      if (!todoId) {
        return;
      }
      if (isApplyingTodoChange) {
        todoCheckbox.checked = !todoCheckbox.checked;
        if (status) {
          status.textContent = 'Please wait for the previous todo update to finish.';
          status.classList.add('error');
        }
        return;
      }
      const todo = allTodos.find((item) => item.id === todoId);
      if (!todo) {
        return;
      }
      const previous = todo.completed;
      todo.completed = todoCheckbox.checked;
      if (todo.completed) {
        todo.completed_at = new Date().toISOString();
      } else {
        delete todo.completed_at;
      }
      isApplyingTodoChange = true;
      for (const input of todoCheckboxes) {
        input.disabled = true;
      }
      void (async () => {
        try {
          const response = await upsertTodosWithRetry(config, session, activeProfile.profile_id, {
            items: buildTodoBatchItems(allTodos),
            replace: true,
          });
          if (!response.session) {
            clearSession(config.storageKey);
            await redirectToLogin(config);
            return;
          }
          saveSession(config.storageKey, response.session);
          const updatedProfiles = updateProfileBalanceFromRewards(profiles, response.todos.rewards);
          const nextTodos =
            response.todos.items.length > 0 ? sortTodosByDisplayOrder(response.todos.items) : sortTodosByDisplayOrder(allTodos);
          await loadAndRenderHabitTracker(
            root,
            config,
            response.session,
            updatedProfiles,
            activeProfile.profile_id,
            weekMeta.startDateKey,
            activeDateView,
            buildTrackerCache(nextTodos),
          );
        } catch (error) {
          todo.completed = previous;
          todoCheckbox.checked = previous;
          if (status) {
            status.textContent = error instanceof Error ? error.message : String(error);
            status.classList.add('error');
          }
        } finally {
          isApplyingTodoChange = false;
          for (const input of todoCheckboxes) {
            input.disabled = false;
          }
        }
      })();
    });
  }

  for (const priorityButton of todoPriorityButtons) {
    priorityButton.addEventListener('click', () => {
      const todoId = priorityButton.dataset.todoId;
      const action = priorityButton.dataset.todoAction;
      if (!todoId || (action !== 'up' && action !== 'down')) {
        return;
      }
      const orderedVisible = allTodos.filter(isVisibleTodo);
      const visibleIndex = orderedVisible.findIndex((todo) => todo.id === todoId);
      if (visibleIndex < 0) {
        return;
      }
      const targetVisibleIndex = action === 'up' ? visibleIndex - 1 : visibleIndex + 1;
      if (targetVisibleIndex < 0 || targetVisibleIndex >= orderedVisible.length) {
        return;
      }
      const swapWithId = orderedVisible[targetVisibleIndex]?.id;
      const currentIndex = allTodos.findIndex((todo) => todo.id === todoId);
      const swapIndex = allTodos.findIndex((todo) => todo.id === swapWithId);
      if (currentIndex < 0 || swapIndex < 0) {
        return;
      }
      [allTodos[currentIndex], allTodos[swapIndex]] = [allTodos[swapIndex], allTodos[currentIndex]];
      void (async () => {
        try {
          const response = await upsertTodosWithRetry(config, session, activeProfile.profile_id, {
            items: buildTodoBatchItems(allTodos),
            replace: true,
          });
          if (!response.session) {
            clearSession(config.storageKey);
            await redirectToLogin(config);
            return;
          }
          saveSession(config.storageKey, response.session);
          const nextTodos =
            response.todos.items.length > 0 ? sortTodosByDisplayOrder(response.todos.items) : sortTodosByDisplayOrder(allTodos);
          await loadAndRenderHabitTracker(
            root,
            config,
            response.session,
            profiles,
            activeProfile.profile_id,
            weekMeta.startDateKey,
            activeDateView,
            buildTrackerCache(nextTodos),
          );
        } catch (error) {
          [allTodos[currentIndex], allTodos[swapIndex]] = [allTodos[swapIndex], allTodos[currentIndex]];
          if (status) {
            status.textContent = error instanceof Error ? error.message : String(error);
            status.classList.add('error');
          }
        }
      })();
    });
  }

  if (printTrackerButton) {
    printTrackerButton.addEventListener('click', () => {
      window.print();
    });
  }

  if (openWithdrawRewardsButton && withdrawDialog) {
    openWithdrawRewardsButton.addEventListener('click', () => {
      const updateWithdrawPreview = (): void => {
        if (!withdrawRemainingTotal || !withdrawAmount) {
          return;
        }
        const amount = Number(withdrawAmount.value);
        const amountCents = Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
        const remainingCents = rewardBalanceCents - amountCents;
        withdrawRemainingTotal.textContent = toCurrencyLabelFromCents(remainingCents);
        withdrawRemainingTotal.classList.toggle('is-negative', remainingCents < 0);
      };

      if (withdrawCurrentTotal) {
        withdrawCurrentTotal.textContent = toCurrencyLabelFromCents(rewardBalanceCents);
      }
      if (withdrawAmount) {
        withdrawAmount.value = '';
      }
      if (withdrawDescription) {
        withdrawDescription.value = '';
      }
      updateWithdrawPreview();

      if (typeof withdrawDialog.showModal === 'function') {
        withdrawDialog.showModal();
      } else {
        withdrawDialog.setAttribute('open', 'true');
      }
    });
  }

  if (cancelWithdrawButton && withdrawDialog) {
    cancelWithdrawButton.addEventListener('click', () => {
      withdrawDialog.close();
    });
  }

  if (withdrawForm && withdrawDialog && withdrawAmount) {
    const updateWithdrawPreview = (): void => {
      if (!withdrawRemainingTotal) {
        return;
      }
      const amount = Number(withdrawAmount.value);
      const amountCents = Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
      const remainingCents = rewardBalanceCents - amountCents;
      withdrawRemainingTotal.textContent = toCurrencyLabelFromCents(remainingCents);
      withdrawRemainingTotal.classList.toggle('is-negative', remainingCents < 0);
    };

    withdrawAmount.addEventListener('input', () => {
      updateWithdrawPreview();
    });

    withdrawForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const amount = Number(withdrawAmount.value);
      const amountCents = Math.round(amount * 100);
      if (!Number.isFinite(amount) || amount <= 0 || !Number.isInteger(amountCents)) {
        return;
      }
      void (async () => {
        try {
          const response = await withdrawRewardsWithRetry(config, session, activeProfile.profile_id, {
            amount_cents: amountCents,
            description: withdrawDescription?.value?.trim() || undefined,
          });
          if (!response.session) {
            clearSession(config.storageKey);
            await redirectToLogin(config);
            return;
          }
          saveSession(config.storageKey, response.session);
          withdrawDialog.close();
          const updatedProfiles = profiles.map((profile) =>
            profile.profile_id === activeProfile.profile_id
              ? { ...profile, reward_balance_cents: profile.reward_balance_cents - amountCents }
              : profile,
          );
          await loadAndRenderHabitTracker(
            root,
            config,
            response.session,
            updatedProfiles,
            activeProfile.profile_id,
            weekMeta.startDateKey,
            activeDateView,
            buildTrackerCache(allTodos),
          );
        } catch (error) {
          if (status) {
            status.textContent = error instanceof Error ? error.message : String(error);
            status.classList.add('error');
          }
        }
      })();
    });
  }

  if (logoutButton) {
    logoutButton.addEventListener('click', () => {
      void logout(config);
    });
  }

  if (activeProfileSelect) {
    activeProfileSelect.addEventListener('change', () => {
      void loadAndRenderHabitTracker(
        root,
        config,
        session,
        profiles,
        activeProfileSelect.value,
        weekMeta.startDateKey,
        activeDateView,
      );
    });
  }

  for (const checkbox of checkboxes) {
    checkbox.addEventListener('change', () => {
      const key = checkbox.dataset.checkKey;
      if (!key || !status) {
        return;
      }
      if (isApplyingHabitChange) {
        checkbox.checked = !checkbox.checked;
        status.textContent = 'Please wait for the previous habit update to finish.';
        status.classList.add('error');
        return;
      }
      const payload = toEntryPayloadFromCheckKey(activeProfile.profile_id, monthMeta, key, checkbox.checked);
      if (!payload) {
        checkbox.checked = !checkbox.checked;
        status.textContent = 'Unable to map this habit tracker cell.';
        status.classList.add('error');
        return;
      }

      const previousValue = !checkbox.checked;
      isApplyingHabitChange = true;
      for (const input of checkboxes) {
        input.disabled = true;
      }
      status.textContent = checkbox.checked ? 'Saving completion...' : 'Removing completion...';
      status.classList.remove('error');
      void (async () => {
        try {
          const response = await upsertHabitEntryWithRetry(config, session, payload);
          if (!response.session) {
            clearSession(config.storageKey);
            await redirectToLogin(config);
            return;
          }
          saveSession(config.storageKey, response.session);
          const matchingHabit = habits.find((habit) => habit.id === payload.habit_id);
          const rewardDeltaCents = matchingHabit ? Math.round(matchingHabit.reward_amount * 100) : 0;
          const signedDeltaCents = payload.completed ? rewardDeltaCents : -rewardDeltaCents;
          const entryMonthKey = payload.entry_date.slice(0, 7);
          const nextEntriesByMonth = cloneEntriesByMonth(trackerCache.entriesByMonth);
          const existingMonthEntries = [...(nextEntriesByMonth[entryMonthKey] ?? [])];
          const isSameEntry = (entry: HabitEntryItem): boolean =>
            entry.habit_id === payload.habit_id &&
            entry.scope === payload.scope &&
            entry.bucket === payload.bucket &&
            entry.entry_date === payload.entry_date;
          const filteredMonthEntries = existingMonthEntries.filter((entry) => !isSameEntry(entry));
          if (payload.completed) {
            filteredMonthEntries.push({
              id: `${payload.entry_date}#${payload.habit_id}#${payload.scope}#${payload.bucket}`,
              profile_id: activeProfile.profile_id,
              habit_id: payload.habit_id,
              scope: payload.scope,
              bucket: payload.bucket,
              entry_date: payload.entry_date,
              completed: true,
              owner_subject: activeProfile.owner_subject,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          }
          nextEntriesByMonth[entryMonthKey] = filteredMonthEntries;
          const updatedProfiles = profiles.map((profile) =>
            profile.profile_id === activeProfile.profile_id
              ? { ...profile, reward_balance_cents: profile.reward_balance_cents + signedDeltaCents }
              : profile,
          );
          await loadAndRenderHabitTracker(
            root,
            config,
            response.session,
            updatedProfiles,
            activeProfile.profile_id,
            weekMeta.startDateKey,
            activeDateView,
            buildTrackerCache(allTodos, nextEntriesByMonth),
          );
        } catch (error) {
          checkbox.checked = previousValue;
          status.textContent = error instanceof Error ? error.message : String(error);
          status.classList.add('error');
        } finally {
          isApplyingHabitChange = false;
          for (const input of checkboxes) {
            input.disabled = false;
          }
        }
      })();
    });
  }
}

async function reloadDashboard(
  root: HTMLElement,
  config: AppConfig,
  status: HTMLElement,
  refreshButton: HTMLButtonElement,
): Promise<void> {
  refreshButton.disabled = true;
  status.textContent = 'Refreshing profiles...';

  try {
    const session = loadSession(config.storageKey);
    if (!session) {
      await redirectToLogin(config);
      return;
    }

    await loadAndRenderProfiles(root, config, session);
  } finally {
    refreshButton.disabled = false;
  }
}

async function createProfileAndRefresh(
  root: HTMLElement,
  config: AppConfig,
  status: HTMLElement,
  form: HTMLFormElement,
): Promise<void> {
  const createButton = document.querySelector<HTMLButtonElement>('#createProfile');
  const descriptionInput = document.querySelector<HTMLInputElement>('#newDescription');
  const iconInput = document.querySelector<HTMLInputElement>('#newIconName');
  const themeInput = document.querySelector<HTMLInputElement>('#newThemeColor');
  const session = loadSession(config.storageKey);

  if (!createButton || !descriptionInput || !iconInput || !themeInput || !session) {
    return;
  }

  const description = descriptionInput.value.trim();
  if (!description) {
    status.textContent = 'Profile name is required to create a profile.';
    return;
  }

  createButton.disabled = true;
  status.textContent = 'Creating profile...';

  try {
    const response = await authorizedJsonRequestWithRetry(config, session, `${config.apiBaseUrl}/profiles`, {
      method: 'POST',
      body: JSON.stringify({
        description,
        icon_name: iconInput.value.trim() || 'account',
        theme_color: themeInput.value.trim() || '#1565C0',
      }),
    });

    if (!response.session) {
      clearSession(config.storageKey);
      await redirectToLogin(config);
      return;
    }

    saveSession(config.storageKey, response.session);
    form.reset();
    await loadAndRenderProfiles(root, config, response.session);
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : String(error);
  } finally {
    createButton.disabled = false;
  }
}

async function callApiWithRetry(
  config: AppConfig,
  status: HTMLElement,
  responseView: HTMLElement,
  button: HTMLButtonElement,
): Promise<void> {
  button.disabled = true;
  status.textContent = 'Calling protected API...';

  try {
    const storedSession = loadSession(config.storageKey);
    if (!storedSession) {
      await redirectToLogin(config);
      return;
    }

    const effectiveSession = await ensureValidSession(config, storedSession);
    if (!effectiveSession) {
      clearSession(config.storageKey);
      await redirectToLogin(config);
      return;
    }

    saveSession(config.storageKey, effectiveSession);
    let response = await fetch(`${config.apiBaseUrl}/userinfo`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${effectiveSession.accessToken}`,
      },
    });

    if (response.status === 401) {
      const refreshed = await refreshSession(config, effectiveSession).catch(() => null);
      if (!refreshed) {
        clearSession(config.storageKey);
        await redirectToLogin(config);
        return;
      }
      saveSession(config.storageKey, refreshed);
      response = await fetch(`${config.apiBaseUrl}/userinfo`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${refreshed.accessToken}`,
        },
      });
    }

    if (!response.ok) {
      throw new Error(await readError(response));
    }

    const data = await response.json();
    responseView.textContent = JSON.stringify(data, null, 2);
    status.textContent = 'API call completed';
  } catch (error) {
    status.textContent = 'API call failed';
    responseView.textContent = error instanceof Error ? error.message : String(error);
  } finally {
    button.disabled = false;
  }
}

async function fetchProfilesWithRetry(
  config: AppConfig,
  session: SessionRecord,
): Promise<{ session: SessionRecord | null; profiles: ProfilesResponse }> {
  const response = await authorizedJsonRequestWithRetry(config, session, `${config.apiBaseUrl}/profiles`, {
    method: 'GET',
  });
  return {
    session: response.session,
    profiles: response.body as ProfilesResponse,
  };
}

async function fetchHabitsWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
): Promise<{ session: SessionRecord | null; habits: HabitsResponse }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/habits?since=1970-01-01T00:00:00.000Z`,
    {
      method: 'GET',
    },
  );
  return {
    session: response.session,
    habits: response.body as HabitsResponse,
  };
}

async function fetchHabitEntriesWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
  month: string,
): Promise<{ session: SessionRecord | null; entries: HabitEntriesResponse }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/habit-entries?month=${encodeURIComponent(month)}`,
    {
      method: 'GET',
    },
  );
  return {
    session: response.session,
    entries: response.body as HabitEntriesResponse,
  };
}

async function fetchTodosWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
): Promise<{ session: SessionRecord | null; todos: TodosResponse }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/todos`,
    {
      method: 'GET',
    },
  );
  return {
    session: response.session,
    todos: response.body as TodosResponse,
  };
}

async function fetchRewardsWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
  limit = 50,
): Promise<{ session: SessionRecord | null; rewards: RewardsResponse }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/rewards?limit=${encodeURIComponent(String(limit))}`,
    {
      method: 'GET',
    },
  );
  return {
    session: response.session,
    rewards: response.body as RewardsResponse,
  };
}

async function upsertHabitEntryWithRetry(
  config: AppConfig,
  session: SessionRecord,
  item: {
    profileId: string;
    habit_id: string;
    scope: 'daily' | 'weekly' | 'monthly';
    bucket: string;
    entry_date: string;
    completed: boolean;
  },
): Promise<{ session: SessionRecord | null; body: unknown }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(item.profileId)}/habit-entries/batch`,
    {
      method: 'PUT',
      body: JSON.stringify({
        items: [
          {
            habit_id: item.habit_id,
            scope: item.scope,
            bucket: item.bucket,
            entry_date: item.entry_date,
            completed: item.completed,
          },
        ],
      }),
    },
  );
  return { session: response.session, body: response.body };
}

async function upsertTodosWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
  payload: {
    items: Array<{
      id?: string;
      name: string;
      reward_amount?: number;
      sort_order?: number;
      due_date?: string;
      completed?: boolean;
    }>;
    replace?: boolean;
  },
): Promise<{ session: SessionRecord | null; todos: UpsertTodosResponse }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/todos/batch`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
  return { session: response.session, todos: response.body as UpsertTodosResponse };
}

async function deleteTodoWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
  todoId: string,
): Promise<{ session: SessionRecord | null; body: unknown }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/todos/${encodeURIComponent(todoId)}`,
    {
      method: 'DELETE',
    },
  );
  return { session: response.session, body: response.body };
}

async function withdrawRewardsWithRetry(
  config: AppConfig,
  session: SessionRecord,
  profileId: string,
  payload: { amount_cents: number; description?: string },
): Promise<{ session: SessionRecord | null; body: unknown }> {
  const response = await authorizedJsonRequestWithRetry(
    config,
    session,
    `${config.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/rewards/withdraw`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return { session: response.session, body: response.body };
}

async function authorizedJsonRequestWithRetry(
  config: AppConfig,
  session: SessionRecord,
  url: string,
  init: RequestInit,
): Promise<{ session: SessionRecord | null; body: unknown }> {
  const activeSession = await ensureValidSession(config, session);
  if (!activeSession) {
    return { session: null, body: null };
  }

  let response = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${activeSession.accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  let finalSession = activeSession;
  if (response.status === 401) {
    const refreshed = await refreshSession(config, activeSession).catch(() => null);
    if (!refreshed) {
      return { session: null, body: null };
    }

    finalSession = refreshed;
    response = await fetch(url, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Authorization: `Bearer ${refreshed.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
  }

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  const body = (await response.json()) as unknown;
  return { session: finalSession, body };
}

async function redirectToLogin(config: AppConfig): Promise<void> {
  const state = crypto.randomUUID();
  const verifier = createCodeVerifier();
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify({ state, verifier }));
  sessionStorage.setItem(RETURN_TO_STORAGE_KEY, window.location.pathname + window.location.search + window.location.hash);

  const challenge = await createCodeChallenge(verifier);
  const authorizeUrl = new URL(`${config.cognitoHostedUiBaseUrl}/oauth2/authorize`);
  authorizeUrl.searchParams.set('client_id', config.cognitoClientId);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('scope', config.scope);
  authorizeUrl.searchParams.set('redirect_uri', config.redirectUri);
  authorizeUrl.searchParams.set('state', state);
  authorizeUrl.searchParams.set('code_challenge', challenge);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');

  window.location.assign(authorizeUrl.toString());
}

async function logout(config: AppConfig): Promise<void> {
  clearSession(config.storageKey);
  clearPkceState();
  const logoutUrl = new URL(`${config.cognitoHostedUiBaseUrl}/logout`);
  logoutUrl.searchParams.set('client_id', config.cognitoClientId);
  logoutUrl.searchParams.set('logout_uri', config.logoutUri);
  window.location.assign(logoutUrl.toString());
}

async function exchangeCodeForTokens(config: AppConfig, code: string, verifier: string): Promise<SessionRecord> {
  const response = await fetch(`${config.cognitoHostedUiBaseUrl}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognitoClientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  const tokenResponse = (await response.json()) as TokenResponse;
  if (!tokenResponse.refresh_token) {
    throw new Error('Login succeeded but no refresh token was returned');
  }

  return normalizeTokenResponse(tokenResponse);
}

async function refreshSession(config: AppConfig, session: SessionRecord): Promise<SessionRecord> {
  const response = await fetch(`${config.cognitoHostedUiBaseUrl}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.cognitoClientId,
      refresh_token: session.refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  const tokenResponse = (await response.json()) as TokenResponse;
  return normalizeTokenResponse(tokenResponse, session.refreshToken);
}

function normalizeTokenResponse(response: TokenResponse, existingRefreshToken?: string): SessionRecord {
  const accessToken = response.access_token;
  const refreshToken = response.refresh_token ?? existingRefreshToken;
  if (!accessToken) {
    throw new Error('Token response did not include an access token');
  }

  if (!refreshToken) {
    throw new Error('Token response did not include a refresh token');
  }

  return {
    accessToken,
    idToken: response.id_token,
    refreshToken,
    tokenType: response.token_type,
    expiresAt: decodeJwtExpiry(accessToken) ?? Date.now() + response.expires_in * 1000,
  };
}

function loadSession(storageKey: string): SessionRecord | null {
  const raw = localStorage.getItem(storageKey);
  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as SessionRecord;
    if (!session.accessToken || !session.refreshToken || !session.expiresAt) {
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function saveSession(storageKey: string, session: SessionRecord): void {
  localStorage.setItem(storageKey, JSON.stringify(session));
}

function clearSession(storageKey: string): void {
  localStorage.removeItem(storageKey);
}

function getPreferredActiveProfileStorageKey(config: AppConfig): string {
  return `${config.storageKey}.preferredActiveProfileId`;
}

function getDashboardRefreshStorageKey(config: AppConfig, profileId: string): string {
  return `${config.storageKey}.dashboardLastRefreshedAt.${profileId}`;
}

function loadDashboardDataRefreshedAt(config: AppConfig, profileId: string): number | null {
  const key = getDashboardRefreshStorageKey(config, profileId);
  const raw = localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function saveDashboardDataRefreshedAt(config: AppConfig, profileId: string): void {
  localStorage.setItem(getDashboardRefreshStorageKey(config, profileId), String(Date.now()));
}

function hasDashboardDataRefreshExpired(config: AppConfig, profileId: string): boolean {
  const lastRefreshedAt = loadDashboardDataRefreshedAt(config, profileId);
  if (!lastRefreshedAt) {
    return true;
  }
  return Date.now() - lastRefreshedAt >= DASHBOARD_AUTO_REFRESH_INTERVAL_MS;
}

function loadPreferredActiveProfileId(config: AppConfig): string | null {
  const value = localStorage.getItem(getPreferredActiveProfileStorageKey(config));
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function savePreferredActiveProfileId(config: AppConfig, profileId: string): void {
  const trimmed = profileId.trim();
  if (!trimmed) {
    return;
  }
  localStorage.setItem(getPreferredActiveProfileStorageKey(config), trimmed);
}

function loadPkceState(): { state: string; verifier: string } | null {
  const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as { state?: string; verifier?: string };
    if (typeof parsed.state !== 'string' || typeof parsed.verifier !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearPkceState(): void {
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
}

function isExpired(session: SessionRecord): boolean {
  return session.expiresAt <= Date.now() + ACCESS_TOKEN_REFRESH_WINDOW_MS;
}

async function ensureValidSession(config: AppConfig, session: SessionRecord): Promise<SessionRecord | null> {
  if (!isExpired(session)) {
    return session;
  }

  const refreshed = await refreshSession(config, session).catch(() => null);
  if (!refreshed) {
    return null;
  }

  saveSession(config.storageKey, refreshed);
  return refreshed;
}

function previewToken(token: string): string {
  return `${token.slice(0, 12)}...${token.slice(-8)}`;
}

function decodeJwtExpiry(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (typeof payload.exp !== 'number') {
      return null;
    }
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function createCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4;
  const padded = padding === 0 ? normalized : `${normalized}${'='.repeat(4 - padding)}`;
  return atob(padded);
}

async function readError(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) {
    return `Request failed with HTTP ${response.status}`;
  }

  try {
    const json = JSON.parse(text) as { message?: string; error?: string };
    return json.message ?? json.error ?? text;
  } catch {
    return text;
  }
}

function replaceUrl(url: string): void {
  window.history.replaceState({}, document.title, url);
  const returnTo = sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
  if (returnTo) {
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    window.history.replaceState({}, document.title, returnTo);
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
